/**
 * MechatScreen — PayCash-compatible encrypted DMs (mechatmechat), with payments.
 *
 * Reading: incoming messages are decrypted with the in-memory unlocked private
 * key (no key paste, nothing leaves the device).
 * Sending: encrypted to the recipient's active key and pushed as `senddm`.
 * Payments: ordinary token transfers, shown inline in the thread — see payments.ts.
 *
 * Our own sent messages can never be read back from the chain (see mechatOutbox),
 * so their plaintext is kept in a local per-device log and merged into the thread.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  MECHAT_COUNTER_WARN_CHARS,
  MECHAT_MAX_MESSAGE_CHARS,
  decryptMessages,
  fetchMessages,
  fetchPayments,
  formatNftMarker,
  sortTokensForDisplay,
  groupConversations,
  parseMessageParts,
  sendMessage,
  sendEncryptedMessage,
  encryptPayCashFile,
  requestPayCashUploadToken,
  uploadPayCashFile,
  buildFileTag,
  resolveRecipientKey,
  transfer,
  validateMessage,
  validateTransfer,
  type ChainPayment,
  type Conversation,
  type DecryptedChainMessage,
  type NftAsset,
  type TokenBalance,
} from '@properwallet/chain-eos'
import type { TranslationKey } from '@properwallet/i18n'
import type { Session } from '@wharfkit/session'
import { useWalletStore } from '../store/wallet.store'
import { useAppStore } from '../store/app.store'
import { useTranslation } from '../hooks/useTranslation'
import { useOnChainAvatars } from '../hooks/useOnChainAvatars'
import { useGatewayImage } from '../hooks/useGatewayImage'
import AccountAvatar from '../components/AccountAvatar'
import { createSessionFromPrivateKey } from '../lib/createSessionFromPrivateKey'
import {
  appendOutbox,
  attachTxid,
  loadOutbox,
  setOutboxText,
  newLocalId,
  outboxByTxid,
  removeFromOutbox,
  type OutboxEntry,
} from '../lib/mechatOutbox'
import {
  countUnread,
  ensureReadStateInitialized,
  markPeerRead,
  type ReadState,
} from '../lib/mechatReadState'
import TokenPicker from '../components/TokenPicker'
import AccountChip from '../components/AccountChip'
import { openReport } from '../lib/mechatReport'
import NftAttachment from '../components/NftAttachment'
import PayCashFileAttachment from '../components/PayCashFileAttachment'
import BottomNav from '../components/BottomNav'

/**
 * Object URLs of files WE just sent, kept only for this app session so our own
 * side can preview them. Our uploaded bytes are encrypted to the recipient and
 * can't be decrypted back, so nothing here is persisted — after a restart an own
 * file shows as a plain "sent" card. Keyed by the uploaded file URL.
 */
const sessionFilePreviews = new Map<string, string>()

const ACCOUNT_RE = /^[a-z1-5.]{1,13}$/

/** Composer textarea sizing — was pinned at rows={1}/40px with no auto-grow,
 * so a wrapped 2-3 line draft just scrolled inside a cramped box. Bumped the
 * starting height a bit and made it grow with content (see the effect below). */
const MECHAT_TEXTAREA_MIN_HEIGHT = 62 // two lines at 15px / 1.35 + padding
const MECHAT_TEXTAREA_MAX_HEIGHT = 160

/** A chain message plus, for our own messages, the plaintext from the local log. */
type ThreadMessage = DecryptedChainMessage & { localText?: string; pending?: boolean }

/** The thread interleaves encrypted messages and plain token transfers. */
type ThreadItem =
  | { kind: 'msg'; at: string; key: string; msg: ThreadMessage }
  | { kind: 'pay'; at: string; key: string; pay: ChainPayment }

export default function MechatScreen() {
  const { t } = useTranslation()
  const { activeAccount, unlockedPrivateKey, activeSession, chatPeer, setChatPeer } = useWalletStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activePeer, setActivePeer] = useState<string | null>(null)
  const [stats, setStats] = useState({ total: 0, decrypted: 0, failed: 0 })
  const [outbox, setOutbox] = useState<OutboxEntry[]>([])
  const [composingNew, setComposingNew] = useState(false)
  const [newPeer, setNewPeer] = useState('')
  const [readState, setReadState] = useState<ReadState>({})
  const [chainMessages, setChainMessages] = useState<DecryptedChainMessage[]>([])
  const [payments, setPayments] = useState<ChainPayment[]>([])
  const setMechatUnread = useAppStore(s => s.setMechatUnread)
  const contactNames = useAppStore(s => s.contactNames)
  const blockedAccounts = useAppStore(s => s.blockedAccounts)
  const toggleBlockedAccount = useAppStore(s => s.toggleBlockedAccount)
  const hiddenChatPeers = useAppStore(s => s.hiddenChatPeers)
  const hideChatPeer = useAppStore(s => s.hideChatPeer)
  const unhideChatPeer = useAppStore(s => s.unhideChatPeer)
  const tosAccepted = useAppStore(s => s.mechatTosAccepted)
  const acceptTos = useAppStore(s => s.acceptMechatTos)
  const revealedChatPeers = useAppStore(s => s.revealedChatPeers)
  const [peerMenu, setPeerMenu] = useState(false)
  const [showHiddenConversations, setShowHiddenConversations] = useState(false)

  const accountName = activeAccount?.name ?? ''

  const load = useCallback(async () => {
    if (!accountName || !unlockedPrivateKey) {
      setError(t('mechat.needUnlock'))
      return
    }
    setLoading(true)
    setError('')
    try {
      // Payments are decoration — they must never break the messages.
      const [raw, paid] = await Promise.all([
        fetchMessages({ account: accountName, limit: 50 }),
        fetchPayments({ account: accountName, limit: 100 }).catch(() => [] as ChainPayment[]),
      ])
      const decrypted = await decryptMessages(raw, unlockedPrivateKey)
      const incoming = decrypted.filter(m => m.direction === 'in')
      const ok = incoming.filter(m => m.text !== null).length
      const failed = incoming.filter(m => m.text === null).length
      setStats({ total: raw.length, decrypted: ok, failed })
      setConversations(groupConversations(decrypted, accountName, { hiddenAccounts: blockedAccounts }))
      setChainMessages(decrypted)
      setPayments(paid)
      const state = ensureReadStateInitialized(accountName, decrypted)
      setReadState(state)
      const counts = countUnread(decrypted, state)
      setMechatUnread(counts.total, counts.byPeer)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setConversations([])
    } finally {
      setLoading(false)
    }
    // intentionally omit `t` — it is recreated every render and caused an infinite get_actions loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountName, unlockedPrivateKey, setMechatUnread, blockedAccounts])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    setOutbox(loadOutbox(accountName))
  }, [accountName])

  /**
   * Conversation list = chain conversations + peers we only ever wrote to.
   * Payments deliberately do NOT open a conversation: otherwise every airdrop and
   * exchange withdrawal would show up as a chat.
   */
  const allPeers = useMemo(() => {
    const names = new Set(conversations.map(c => c.counterparty))
    for (const e of outbox) names.add(e.to)
    return [...names].filter(n => !blockedAccounts.includes(n))
  }, [conversations, outbox, blockedAccounts])

  const hiddenPeerSet = useMemo(() => new Set(hiddenChatPeers), [hiddenChatPeers])

  const previews = useMemo(() => {
    const byTxid = outboxByTxid(outbox)
    return allPeers
      .map(peer => {
        const conv = conversations.find(c => c.counterparty === peer)
        const chainLast = conv?.messages[conv.messages.length - 1]
        const localLast = [...outbox].filter(e => e.to === peer).pop()
        const chainAt = chainLast ? new Date(chainLast.timestamp).getTime() : 0
        const localAt = localLast?.sentAt ?? 0
        const useLocal = localAt > chainAt
        let text: string
        if (useLocal) {
          text = localLast?.text ?? ''
        } else if (chainLast?.direction === 'out') {
          text = byTxid.get(chainLast.trxId)?.text ?? t('mechat.outgoing')
        } else {
          text =
            chainLast?.text ??
            (chainLast?.error === 'wrong-key' ? t('mechat.wrongKey') : t('mechat.failed'))
        }
        return { peer, text, at: Math.max(chainAt, localAt) }
      })
      .sort((a, b) => b.at - a.at)
  }, [allPeers, conversations, outbox, t])

  const visiblePreviews = useMemo(() => {
    if (showHiddenConversations) return previews
    return previews.filter(p => !hiddenPeerSet.has(p.peer))
  }, [previews, showHiddenConversations, hiddenPeerSet])

  const hasHiddenConversations = hiddenChatPeers.some(peer => allPeers.includes(peer))

  useEffect(() => {
    if (!hasHiddenConversations) setShowHiddenConversations(false)
  }, [hasHiddenConversations])

  // Avatars come from the contract
  // a picture next to their own name without owning the NFT it points at.
  const peerAvatars = useOnChainAvatars(allPeers)

  const unreadByPeer = useMemo(() => {
    const all = countUnread(chainMessages, readState).byPeer
    for (const blocked of blockedAccounts) delete all[blocked]
    return all
  }, [chainMessages, readState, blockedAccounts])

  /** Opening a conversation marks everything currently in it as read. */
  const openPeer = useCallback(
    (peer: string) => {
      setActivePeer(peer)
      const state = markPeerRead(accountName, peer, chainMessages)
      setReadState(state)
      const counts = countUnread(chainMessages, state)
      setMechatUnread(counts.total, counts.byPeer)
    },
    [accountName, chainMessages, setMechatUnread],
  )

  // Opened from an account chip elsewhere in the app
  useEffect(() => {
    if (!chatPeer) return
    openPeer(chatPeer)
    setChatPeer(null)
  }, [chatPeer, openPeer, setChatPeer])

  // Keep the open conversation marked read as new messages arrive
  useEffect(() => {
    if (!activePeer || chainMessages.length === 0) return
    const state = markPeerRead(accountName, activePeer, chainMessages)
    setReadState(state)
    const counts = countUnread(chainMessages, state)
    setMechatUnread(counts.total, counts.byPeer)
  }, [activePeer, chainMessages, accountName, setMechatUnread])

  /** Chain thread + local sent log + payments with this peer, chronological. */
  const activeThread = useMemo<ThreadItem[]>(() => {
    if (!activePeer) return []
    const byTxid = outboxByTxid(outbox)
    const conv = conversations.find(c => c.counterparty === activePeer)

    const chainItems: ThreadItem[] = (conv?.messages ?? []).map(m => ({
      kind: 'msg' as const,
      at: m.timestamp,
      key: `m:${m.trxId}:${m.timestamp}`,
      msg: m.direction === 'out' ? { ...m, localText: byTxid.get(m.trxId)?.text } : m,
    }))

    const onChain = new Set((conv?.messages ?? []).map(m => m.trxId))
    // Messages we sent that Hyperion has not indexed yet
    const pendingItems: ThreadItem[] = outbox
      .filter(e => e.to === activePeer && (!e.txid || !onChain.has(e.txid)))
      .map(e => ({
        kind: 'msg' as const,
        at: new Date(e.sentAt).toISOString(),
        key: `p:${e.localId}`,
        msg: {
          trxId: e.txid || e.localId,
          blockNum: 0,
          timestamp: new Date(e.sentAt).toISOString(),
          from: accountName,
          to: e.to,
          direction: 'out' as const,
          iv: '',
          ephemPubKey: '',
          cipherText: '',
          mac: '',
          text: null,
          error: 'outgoing' as const,
          localText: e.text,
          pending: true,
        },
      }))

    const payItems: ThreadItem[] = payments
      .filter(p => p.from === activePeer || p.to === activePeer)
      .map(p => ({ kind: 'pay' as const, at: p.timestamp, key: `t:${p.trxId}:${p.quantity}`, pay: p }))

    return [...chainItems, ...pendingItems, ...payItems].sort((a, b) => a.at.localeCompare(b.at))
  }, [activePeer, conversations, outbox, payments, accountName])

  /**
   * Media from a peer we have never written to is not loaded automatically —
   * see NftAttachment for why.
   */
  const peerTrusted = useMemo(
    () =>
      activePeer
        ? outbox.some(e => e.to === activePeer) || revealedChatPeers.includes(activePeer)
        : false,
    [activePeer, outbox, revealedChatPeers],
  )

  const handleSent = useCallback(
    (entries: OutboxEntry[]) => {
      setOutbox(entries)
      // Give Hyperion a moment, then pull the canonical copy in.
      setTimeout(() => void load(), 4000)
    },
    [load],
  )

  const startNewChat = () => {
    const peer = newPeer.trim().toLowerCase()
    if (!ACCOUNT_RE.test(peer)) {
      setError(t('mechat.recipientInvalid'))
      return
    }
    setError('')
    setComposingNew(false)
    setNewPeer('')
    openPeer(peer)
  }

  // Play policy: terms must be accepted before any user-generated content is sent.
  // It doubles as the place to be honest about what a public blockchain means.
  if (!tosAccepted) {
    return (
      <div className="flex flex-col h-full" style={{ background: '#000000' }}>
        <div
          className="pt-safe px-4 pb-3 flex-shrink-0"
          style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          <h2 className="text-white font-bold" style={{ fontSize: 20 }}>
            {t('mechat.tosTitle')}
          </h2>
        </div>
        <div className="scroll-area flex-1 px-4 py-4 space-y-3">
          {[t('mechat.tosChain'), t('mechat.tosKey'), t('mechat.tosRules')].map((line, i) => (
            <p
              key={i}
              className="text-sm"
              style={{
                color: '#c8d6c9',
                background: '#0c100c',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 14,
                padding: '12px 14px',
                lineHeight: 1.45,
              }}
            >
              {line}
            </p>
          ))}
        </div>
        <div className="px-4 pb-3 flex-shrink-0">
          <button
            type="button"
            className="btn-primary w-full"
            style={{ height: 44, fontSize: 14 }}
            onClick={acceptTos}
          >
            {t('mechat.tosAccept')}
          </button>
        </div>
        <BottomNav />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full" style={{ background: '#000000' }}>
      <div
        className="pt-safe px-4 pb-3 flex-shrink-0"
        style={{ background: '#0c100c', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex items-center gap-2">
            {activePeer ? (
              <AccountAvatar avatarUrl={peerAvatars[activePeer]} size="sm" />
            ) : null}
            <div className="min-w-0">
              <h2 className="text-white font-bold truncate" style={{ fontSize: 20 }}>
                {activePeer ? (contactNames[activePeer] ?? activePeer) : t('mechat.title')}
              </h2>
              <p className="text-xs truncate" style={{ color: '#6b7280' }}>
                {activePeer ? activePeer : `${accountName} · ${t('mechat.subtitle')}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {activePeer ? (
              <>
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ padding: '6px 10px', fontSize: 12, height: 34 }}
                  onClick={() => setActivePeer(null)}
                >
                  ←
                </button>
                <span style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="btn-ghost"
                    style={{ padding: '6px 10px', fontSize: 12, height: 34 }}
                    title={t('mechat.menu')}
                    onClick={() => setPeerMenu(v => !v)}
                  >
                    ⋯
                  </button>
                  {peerMenu && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        right: 0,
                        zIndex: 40,
                        minWidth: 200,
                        borderRadius: 12,
                        background: '#141914',
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 10px 28px rgba(0,0,0,0.55)',
                        padding: 4,
                        display: 'block',
                      }}
                    >
                      <button
                        type="button"
                        className="block w-full text-left"
                        style={{ padding: '8px 10px', fontSize: 13, color: '#e5e7eb', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => {
                          setPeerMenu(false)
                          hideChatPeer(activePeer)
                          setActivePeer(null)
                        }}
                      >
                        {t('mechat.hideConversation')}
                      </button>
                      <button
                        type="button"
                        className="block w-full text-left"
                        style={{ padding: '8px 10px', fontSize: 13, color: '#e5e7eb', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => {
                          setPeerMenu(false)
                          toggleBlockedAccount(activePeer)
                          setActivePeer(null)
                        }}
                      >
                        {t('mechat.block')}
                      </button>
                      <button
                        type="button"
                        className="block w-full text-left"
                        style={{ padding: '8px 10px', fontSize: 13, color: '#f87171', background: 'none', border: 'none', borderRadius: 8, cursor: 'pointer' }}
                        onClick={() => {
                          setPeerMenu(false)
                          openReport({
                            reporter: accountName,
                            reported: activePeer,
                            messageIds: chainMessages
                              .filter(msg => msg.from === activePeer)
                              .slice(0, 10)
                              .map(msg => msg.trxId),
                          })
                          // Reporting also blocks — the user should not have to
                          // keep seeing what they just reported.
                          if (!blockedAccounts.includes(activePeer)) toggleBlockedAccount(activePeer)
                          setActivePeer(null)
                        }}
                      >
                        {t('mechat.report')}
                      </button>
                      <span className="block px-2.5 pb-1.5 pt-0.5 text-[10px]" style={{ color: '#6b7280' }}>
                        {t('mechat.reportHint')}
                      </span>
                    </span>
                  )}
                </span>
              </>
            ) : (
              <>
                {hasHiddenConversations && (
                  <button
                    type="button"
                    className="btn-ghost"
                    style={{ padding: '6px 10px', fontSize: 12, height: 34 }}
                    onClick={() => setShowHiddenConversations(v => !v)}
                  >
                    {showHiddenConversations ? t('mechat.showLess') : t('mechat.showAll')}
                  </button>
                )}
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ padding: '6px 10px', fontSize: 12, height: 34 }}
                  onClick={() => setComposingNew(v => !v)}
                  title={t('mechat.newChat')}
                >
                  +
                </button>
              </>
            )}
            <button
              type="button"
              className="btn-ghost"
              style={{ padding: '6px 10px', fontSize: 12, height: 34 }}
              onClick={() => void load()}
              disabled={loading}
            >
              {loading ? '…' : t('common.retry')}
            </button>
          </div>
        </div>

        {!activePeer && !composingNew && (
          <p className="text-[11px] mt-2" style={{ color: '#6b7280' }}>
            in {stats.decrypted} ok · {stats.failed} fail · {stats.total} total
          </p>
        )}

        {!activePeer && composingNew && (
          <div className="flex gap-2 mt-3">
            <input
              className="input-field flex-1"
              style={{ height: 38, fontSize: 14 }}
              placeholder={t('mechat.recipient')}
              value={newPeer}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              maxLength={13}
              onChange={e => setNewPeer(e.target.value.toLowerCase())}
              onKeyDown={e => {
                if (e.key === 'Enter') startNewChat()
              }}
            />
            <button
              type="button"
              className="btn-primary"
              style={{ height: 38, padding: '0 16px', fontSize: 13 }}
              onClick={startNewChat}
            >
              {t('mechat.startChat')}
            </button>
          </div>
        )}
      </div>

      <div className="scroll-area flex-1 px-4 py-3">
        {!unlockedPrivateKey ? (
          <p className="text-sm text-center py-10" style={{ color: '#f87171' }}>
            {t('mechat.needUnlock')}
          </p>
        ) : error && !activePeer ? (
          <p className="text-sm text-center py-10 break-all" style={{ color: '#f87171' }}>
            {error}
          </p>
        ) : loading && !activePeer ? (
          <p className="text-sm text-center py-10" style={{ color: '#6b7280' }}>
            {t('common.loading')}
          </p>
        ) : activePeer ? (
          <MessageThread
            items={activeThread}
            accountName={accountName}
            trusted={peerTrusted}
            peerAvatarUrl={activePeer ? peerAvatars[activePeer] : undefined}
            ownAvatarUrl={activeAccount?.avatarUrl}
          />
        ) : visiblePreviews.length === 0 ? (
          <p className="text-sm text-center py-10" style={{ color: '#6b7280' }}>
            {hasHiddenConversations && !showHiddenConversations
              ? t('mechat.allHidden')
              : t('mechat.empty')}
          </p>
        ) : (
          <div className="space-y-2">
            {visiblePreviews.map(p => {
              const isHidden = hiddenPeerSet.has(p.peer)
              return (
              <div
                key={p.peer}
                className="flex items-stretch gap-2"
              >
              <button
                type="button"
                className="flex-1 text-left min-w-0"
                style={{
                  background: '#0c100c',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: 14,
                  padding: '12px 14px',
                  opacity: isHidden ? 0.55 : 1,
                }}
                onClick={() => openPeer(p.peer)}
              >
                <div className="flex justify-between gap-2 mb-1">
                  <span className="text-white font-semibold text-sm flex items-center gap-1.5 min-w-0">
                    <AccountAvatar avatarUrl={peerAvatars[p.peer]} size="xs" />
                    {unreadByPeer[p.peer] ? (
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#43a047',
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <span className="truncate">
                      {contactNames[p.peer] ? `${contactNames[p.peer]} (${p.peer})` : p.peer}
                    </span>
                    {isHidden ? (
                      <span
                        className="flex-shrink-0 text-[10px] font-medium uppercase tracking-wide"
                        style={{
                          color: '#9ca3af',
                          background: 'rgba(255,255,255,0.06)',
                          borderRadius: 6,
                          padding: '1px 6px',
                        }}
                      >
                        {t('mechat.hiddenBadge')}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-[10px] flex items-center gap-1.5" style={{ color: '#6b7280' }}>
                    {unreadByPeer[p.peer] ? (
                      <span
                        style={{
                          background: '#43a047',
                          color: '#04140a',
                          borderRadius: 9,
                          padding: '0 6px',
                          fontSize: 10,
                          fontWeight: 700,
                          lineHeight: '16px',
                        }}
                      >
                        {unreadByPeer[p.peer]}
                      </span>
                    ) : null}
                    {p.at ? new Date(p.at).toLocaleString() : ''}
                  </span>
                </div>
                <p className="text-xs truncate" style={{ color: '#9ca3af' }}>
                  {p.text}
                </p>
              </button>
              {isHidden && showHiddenConversations ? (
                <button
                  type="button"
                  className="btn-ghost flex-shrink-0"
                  style={{
                    padding: '0 10px',
                    fontSize: 11,
                    height: 'auto',
                    alignSelf: 'stretch',
                    borderRadius: 14,
                    maxWidth: 88,
                    lineHeight: 1.25,
                  }}
                  onClick={() => unhideChatPeer(p.peer)}
                >
                  {t('mechat.unhide')}
                </button>
              ) : !isHidden ? (
                <button
                  type="button"
                  className="btn-ghost flex-shrink-0"
                  style={{
                    padding: '0 10px',
                    fontSize: 11,
                    height: 'auto',
                    alignSelf: 'stretch',
                    borderRadius: 14,
                    maxWidth: 72,
                    lineHeight: 1.25,
                    color: '#9ca3af',
                  }}
                  title={t('mechat.hideConversation')}
                  onClick={() => hideChatPeer(p.peer)}
                >
                  {t('mechat.hideShort')}
                </button>
              ) : null}
              </div>
            )})}
          </div>
        )}
      </div>

      {activePeer && (
        <Composer
          accountName={accountName}
          peer={activePeer}
          session={activeSession}
          privateKey={unlockedPrivateKey}
          onSent={handleSent}
        />
      )}

      <BottomNav />
    </div>
  )
}

/** Message input, character countdown, and the payment attachment. */
function Composer({
  accountName,
  peer,
  session,
  privateKey,
  onSent,
}: {
  accountName: string
  peer: string
  session: Session | null
  privateKey: string | null
  onSent: (entries: OutboxEntry[]) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState('')
  const [payOpen, setPayOpen] = useState(false)
  const [nftOpen, setNftOpen] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  const remaining = MECHAT_MAX_MESSAGE_CHARS - draft.length
  const warn = remaining <= MECHAT_COUNTER_WARN_CHARS
  const canSend = !sending && validateMessage({ from: accountName, to: peer, text: draft }) === null

  // Auto-grow the textarea with the content instead of staying pinned at one
  // line height — rows={1} alone never resizes, so long/wrapped drafts used to
  // just scroll inside a cramped 40px box. Caps at MECHAT_TEXTAREA_MAX_HEIGHT,
  // matching the textarea's own maxHeight below.
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, MECHAT_TEXTAREA_MAX_HEIGHT)}px`
  }, [draft])

  const resolveSession = (): Session => {
    const active = session ?? (privateKey ? createSessionFromPrivateKey(privateKey, accountName) : null)
    if (!active) throw new Error(t('mechat.needUnlock'))
    return active
  }

  /** Attaching appends a marker to the draft — it is just text inside the message. */
  const attachNft = (asset: NftAsset) => {
    const marker = formatNftMarker(asset.assetId)
    setDraft(prev => {
      const next = prev.trim().length > 0 ? `${prev.trimEnd()} ${marker}` : marker
      return next.slice(0, MECHAT_MAX_MESSAGE_CHARS)
    })
    setNftOpen(false)
    textareaRef.current?.focus()
  }

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const MAX_FILE_BYTES = 1 * 1024 * 1024

  /**
   * Sends a file the PayCash way: encrypt the bytes to the recipient, upload to
   * PayCash's store, then send the <file> tag sealed under the SAME ephemeral key
   * (see chain-eos encryptPayCashFile / paycashFiles).
   */
  const sendFile = async (file: File) => {
    if (sending) return
    if (!privateKey) {
      setSendError(t('mechat.needUnlock'))
      return
    }
    if (file.size > MAX_FILE_BYTES) {
      setSendError(t('mechat.fileTooLarge'))
      return
    }
    setSending(true)
    setSendError('')
    const localId = newLocalId()
    // Our own uploaded bytes are encrypted to the recipient, so we can't render
    // them back — show a sent-file label locally (the local-only outbox, same as
    // text). The recipient receives the real <file> attachment.
    let entries = appendOutbox(accountName, {
      txid: '',
      to: peer,
      text: `📎 ${file.name}`,
      sentAt: Date.now(),
      localId,
    })
    onSent(entries)
    try {
      const bytes = new Uint8Array(await file.arrayBuffer())
      const pubKey = (await resolveRecipientKey(peer)).publicKey
      const sealCtx = await encryptPayCashFile(bytes, pubKey)
      const authorization = await requestPayCashUploadToken({ account: accountName, privateKey })
      const contentType = file.type || 'application/octet-stream'
      const url = await uploadPayCashFile({
        authorization,
        encryptedFile: sealCtx.encryptedFile,
        fileName: file.name,
        contentType,
      })
      const ext = file.name.includes('.') ? (file.name.split('.').pop() ?? '') : ''
      const tag = buildFileTag({ url, fileName: file.name, fileSize: file.size, fileExt: ext })
      // Render the real file card on our own side too. Keep an in-session preview
      // for images (nothing persisted — see sessionFilePreviews).
      const isImg = /^(jpg|jpeg|png|gif|webp|bmp|heic)$/i.test(ext) || file.type.startsWith('image/')
      if (isImg) sessionFilePreviews.set(url, URL.createObjectURL(file))
      entries = setOutboxText(accountName, localId, tag)
      onSent(entries)
      const encrypted = await sealCtx.seal(tag)
      const result = await sendEncryptedMessage(resolveSession(), { from: accountName, to: peer, encrypted })
      entries = attachTxid(accountName, localId, result.txid)
      onSent(entries)
    } catch (err) {
      entries = removeFromOutbox(accountName, localId)
      onSent(entries)
      const message = err instanceof Error ? err.message : String(err)
      setSendError(
        message.includes('no public key')
          ? t('mechat.noRecipientKey')
          : `${t('mechat.sendFailed')}: ${message}`,
      )
    } finally {
      setSending(false)
    }
  }

  const send = async () => {
    if (!canSend) return
    const text = draft
    setSending(true)
    setSendError('')

    const localId = newLocalId()
    // Show it immediately — the local log is the only place this text will ever live.
    let entries = appendOutbox(accountName, { txid: '', to: peer, text, sentAt: Date.now(), localId })
    onSent(entries)
    setDraft('')

    try {
      const result = await sendMessage(resolveSession(), { from: accountName, to: peer, text })
      entries = attachTxid(accountName, localId, result.txid)
      onSent(entries)
    } catch (err) {
      // Never leave a message in the log that was not actually sent.
      entries = removeFromOutbox(accountName, localId)
      onSent(entries)
      setDraft(text)
      const message = err instanceof Error ? err.message : String(err)
      setSendError(
        message.includes('no public key')
          ? t('mechat.noRecipientKey')
          : `${t('mechat.sendFailed')}: ${message}`,
      )
    } finally {
      setSending(false)
      textareaRef.current?.focus()
    }
  }

  return (
    <div
      className="flex-shrink-0"
      style={{ background: '#0c100c', borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      {nftOpen && <NftPickerSheet onPick={attachNft} onClose={() => setNftOpen(false)} />}

      {payOpen && (
        <PaymentPanel
          accountName={accountName}
          peer={peer}
          resolveSession={resolveSession}
          onClose={() => setPayOpen(false)}
          onPaid={() => {
            setPayOpen(false)
            onSent(loadOutbox(accountName))
          }}
        />
      )}

      <div className="px-4 py-3">
        {sendError && (
          <p className="text-[11px] mb-2 break-all" style={{ color: '#f87171' }}>
            {sendError}
          </p>
        )}
        {/* Composer: full-width 2-line text field on top, action buttons +
            Send in a row below (was a single cramped row where 3 icon
            buttons + Send left the textarea a sliver of the width). */}
        <div className="flex flex-col gap-2">
          <textarea
            ref={textareaRef}
            className="input-field w-full"
            style={{
              minHeight: MECHAT_TEXTAREA_MIN_HEIGHT,
              maxHeight: MECHAT_TEXTAREA_MAX_HEIGHT,
              padding: '10px 12px',
              fontSize: 15,
              resize: 'none',
              lineHeight: 1.35,
              overflowY: 'auto',
            }}
            rows={2}
            placeholder={t('mechat.placeholder')}
            value={draft}
            maxLength={MECHAT_MAX_MESSAGE_CHARS}
            disabled={sending}
            onChange={e => setDraft(e.target.value.slice(0, MECHAT_MAX_MESSAGE_CHARS))}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void send()
              }
            }}
          />
          <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label={t('mechat.attachPayment')}
            title={t('mechat.attachPayment')}
            onClick={() => setPayOpen(v => !v)}
            style={{
              height: 40,
              width: 40,
              flexShrink: 0,
              borderRadius: 12,
              border: `1px solid ${payOpen ? 'rgba(67,160,71,0.5)' : 'rgba(255,255,255,0.08)'}`,
              background: payOpen ? 'rgba(67,160,71,0.15)' : 'transparent',
              color: payOpen ? '#5cbf63' : '#9ca3af',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M14.5 9.5a2.5 2.5 0 0 0-2.5-1.5c-1.4 0-2.5.7-2.5 2s1.1 1.8 2.5 2 2.5.7 2.5 2-1.1 2-2.5 2a2.5 2.5 0 0 1-2.5-1.5" />
              <path d="M12 6.5v11" />
            </svg>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={e => {
              const f = e.target.files?.[0]
              e.target.value = ''
              if (f) void sendFile(f)
            }}
          />
          <button
            type="button"
            aria-label={t('mechat.attachFile')}
            title={t('mechat.attachFile')}
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            style={{
              height: 40,
              width: 40,
              flexShrink: 0,
              borderRadius: 12,
              border: '1px solid rgba(255,255,255,0.08)',
              background: 'transparent',
              color: '#9ca3af',
              cursor: sending ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24l-9.2 9.19a1 1 0 0 1-1.41-1.41l8.49-8.49" />
            </svg>
          </button>
          <button
            type="button"
            aria-label={t('mechat.attachNft')}
            title={t('mechat.attachNft')}
            onClick={() => setNftOpen(v => !v)}
            style={{
              height: 40,
              width: 40,
              flexShrink: 0,
              borderRadius: 12,
              border: `1px solid ${nftOpen ? 'rgba(67,160,71,0.5)' : 'rgba(255,255,255,0.08)'}`,
              background: nftOpen ? 'rgba(67,160,71,0.15)' : 'transparent',
              color: nftOpen ? '#5cbf63' : '#9ca3af',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </button>
          <div className="flex-1" />
          <button
            type="button"
            className="btn-primary"
            style={{ height: 40, padding: '0 22px', fontSize: 14, opacity: canSend ? 1 : 0.5 }}
            disabled={!canSend}
            onClick={() => void send()}
          >
            {sending ? t('mechat.sending') : t('mechat.send')}
          </button>
          </div>
        </div>
        <div className="flex justify-between items-center mt-1.5 gap-2">
          <span className="text-[10px] truncate" style={{ color: '#4b5563' }}>
            {t('mechat.localOnly')}
          </span>
          <span
            className="text-[11px] flex-shrink-0 tabular-nums"
            style={{ color: warn ? '#f87171' : '#6b7280', fontWeight: warn ? 600 : 400 }}
          >
            {remaining} {t('mechat.charsLeft')}
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Token transfer straight from the conversation.
 *
 * Unlike a message, this moves money — so it always goes through an explicit
 * confirmation step showing the exact amount and recipient.
 */
function PaymentPanel({
  accountName,
  peer,
  resolveSession,
  onClose,
  onPaid,
}: {
  accountName: string
  peer: string
  resolveSession: () => Session
  onClose: () => void
  onPaid: () => void
}) {
  const { t } = useTranslation()
  const balances = useWalletStore(s => s.balances)
  const refreshBalances = useWalletStore(s => s.refreshBalances)
  const [token, setToken] = useState<TokenBalance | null>(null)
  const [amount, setAmount] = useState('')
  const [memo, setMemo] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const sendable = useMemo(() => sortTokensForDisplay(balances.filter(b => b.amountRaw > 0)), [balances])

  useEffect(() => {
    if (!token && sendable.length > 0) setToken(sendable[0])
  }, [sendable, token])

  const params = token
    ? {
        from: accountName,
        to: peer,
        amount: Number.parseFloat(amount),
        symbol: token.symbol,
        contract: token.contract,
        precision: token.precision,
        memo,
      }
    : null

  const review = () => {
    if (!token || !params) {
      setError(t('transfer.errors.noToken'))
      return
    }
    const invalid = validateTransfer(params, token.amountRaw)
    if (invalid) {
      setError(t(`transfer.errors.${invalid}` as TranslationKey))
      return
    }
    setError('')
    setConfirming(true)
  }

  const confirm = async () => {
    if (!params) return
    setBusy(true)
    setError('')
    try {
      await transfer(resolveSession(), params)
      setAmount('')
      setMemo('')
      setConfirming(false)
      void refreshBalances()
      onPaid()
    } catch (err) {
      setConfirming(false)
      setError(`${t('mechat.paymentFailed')}: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="px-4 pt-3 pb-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white text-sm font-semibold">{t('mechat.sendToken')}</span>
        <button
          type="button"
          className="btn-ghost"
          style={{ padding: '4px 10px', fontSize: 12, height: 28 }}
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      {sendable.length === 0 ? (
        <p className="text-xs py-3" style={{ color: '#6b7280' }}>
          {t('mechat.noTokens')}
        </p>
      ) : confirming && token ? (
        <div className="pb-3">
          <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>
            {t('mechat.confirmTitle')}
          </p>
          <p className="text-white text-lg font-semibold">
            {Number.parseFloat(amount).toFixed(token.precision)} {token.symbol}
          </p>
          <p className="text-xs mb-3" style={{ color: '#9ca3af' }}>
            → {peer} · {token.contract}
            {memo ? ` · ${memo}` : ''}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost flex-1"
              style={{ height: 38, fontSize: 13 }}
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn-primary flex-1"
              style={{ height: 38, fontSize: 13 }}
              onClick={() => void confirm()}
              disabled={busy}
            >
              {busy ? t('mechat.sending') : t('mechat.confirmSend')}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 pb-3">
          <TokenPicker tokens={sendable} selected={token} onSelect={setToken} />
          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                className="input-field w-full pr-14"
                style={{ height: 38, fontSize: 14 }}
                inputMode="decimal"
                placeholder={t('mechat.amount')}
                value={amount}
                onChange={e => setAmount(e.target.value.replace(',', '.'))}
              />
              <button
                type="button"
                onClick={() => token && setAmount(token.amount)}
                className="absolute"
                style={{
                  right: 8,
                  top: 8,
                  height: 22,
                  padding: '0 8px',
                  fontSize: 11,
                  borderRadius: 8,
                  border: '1px solid rgba(67,160,71,0.35)',
                  background: 'rgba(67,160,71,0.12)',
                  color: '#5cbf63',
                  cursor: 'pointer',
                }}
              >
                {t('mechat.max')}
              </button>
            </div>
            <button
              type="button"
              className="btn-primary"
              style={{ height: 38, padding: '0 16px', fontSize: 13 }}
              onClick={review}
            >
              {t('mechat.send')}
            </button>
          </div>
          <input
            className="input-field w-full"
            style={{ height: 34, fontSize: 12 }}
            placeholder={t('mechat.memoOptional')}
            value={memo}
            maxLength={256}
            onChange={e => setMemo(e.target.value)}
          />
          {token && (
            <p className="text-[10px]" style={{ color: '#4b5563' }}>
              {token.amount} {token.symbol}
            </p>
          )}
        </div>
      )}

      {error && (
        <p className="text-[11px] pb-2 break-all" style={{ color: '#f87171' }}>
          {error}
        </p>
      )}
    </div>
  )
}

function MessageThread({
  items,
  accountName,
  trusted,
  peerAvatarUrl,
  ownAvatarUrl,
}: {
  items: ThreadItem[]
  accountName: string
  trusted: boolean
  peerAvatarUrl?: string
  ownAvatarUrl?: string
}) {
  const { t } = useTranslation()
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [items.length])

  if (items.length === 0) {
    return (
      <p className="text-sm text-center py-10" style={{ color: '#6b7280' }}>
        {t('mechat.empty')}
      </p>
    )
  }

  return (
    <div className="space-y-2 pb-4">
      {items.map(item =>
        item.kind === 'pay' ? (
          <PaymentBubble
            key={item.key}
            pay={item.pay}
            avatarUrl={item.pay.direction === 'out' ? ownAvatarUrl : peerAvatarUrl}
          />
        ) : (
          <MessageBubble
            key={item.key}
            msg={item.msg}
            accountName={accountName}
            trusted={trusted}
            avatarUrl={item.msg.from === accountName ? ownAvatarUrl : peerAvatarUrl}
          />
        ),
      )}
      <div ref={bottomRef} />
    </div>
  )
}

function MessageBubble({
  msg,
  accountName,
  trusted,
  avatarUrl,
}: {
  msg: ThreadMessage
  accountName: string
  trusted: boolean
  avatarUrl?: string
}) {
  const { t } = useTranslation()
  const revealChatPeer = useAppStore(s => s.revealChatPeer)
  const mine = msg.from === accountName
  let body: string
  if (mine) body = msg.localText ?? t('mechat.outgoing')
  else if (msg.text) body = msg.text
  else if (msg.error === 'wrong-key') body = t('mechat.wrongKey')
  else body = t('mechat.failed')

  return (
    <div className={`flex items-end gap-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
      {!mine ? <AccountAvatar avatarUrl={avatarUrl} size="xs" /> : null}
      <div
        style={{
          maxWidth: '85%',
          borderRadius: 14,
          padding: '10px 12px',
          background: mine ? 'rgba(67,160,71,0.15)' : '#141914',
          border: `1px solid ${mine ? 'rgba(67,160,71,0.3)' : 'rgba(255,255,255,0.06)'}`,
          opacity: msg.pending ? 0.65 : 1,
        }}
      >
        <div className="space-y-1.5">
          {parseMessageParts(body).map((part, i) =>
            part.kind === 'nft' ? (
              <NftAttachment
                key={`nft-${part.assetId}-${i}`}
                assetId={part.assetId}
                trusted={mine || trusted}
                senderAccount={msg.from}
                onReveal={() => revealChatPeer(msg.from)}
              />
            ) : part.kind === 'file' ? (
              <PayCashFileAttachment
                key={`file-${part.url}-${i}`}
                url={part.url}
                fileName={part.fileName}
                fileSize={part.fileSize}
                fileExt={part.fileExt}
                message={{ iv: msg.iv, ephemPubKey: msg.ephemPubKey }}
                trusted={mine || trusted}
                ownMessage={mine}
                localPreviewUrl={mine ? sessionFilePreviews.get(part.url) : undefined}
                onReveal={() => revealChatPeer(msg.from)}
              />
            ) : part.text.trim().length > 0 ? (
              <p key={`txt-${i}`} className="text-sm text-white whitespace-pre-wrap break-words">
                {part.text}
              </p>
            ) : null,
          )}
        </div>
        <p className="text-[10px] mt-1" style={{ color: '#6b7280' }}>
          {new Date(msg.timestamp).toLocaleString()}
          {msg.pending ? ` · ${t('mechat.pending')}` : ''}
          {!msg.pending && msg.error && msg.error !== 'outgoing' ? ` · ${msg.error}` : ''}
        </p>
      </div>
      {mine ? <AccountAvatar avatarUrl={avatarUrl} size="xs" /> : null}
    </div>
  )
}

/** A token transfer rendered inside the conversation. */
function PaymentBubble({ pay, avatarUrl }: { pay: ChainPayment; avatarUrl?: string }) {
  const { t } = useTranslation()
  const mine = pay.direction === 'out'
  return (
    <div className={`flex items-end gap-1.5 ${mine ? 'justify-end' : 'justify-start'}`}>
      {!mine ? <AccountAvatar avatarUrl={avatarUrl} size="xs" /> : null}
      <div
        style={{
          maxWidth: '85%',
          borderRadius: 14,
          padding: '10px 12px',
          background: 'rgba(67,160,71,0.08)',
          border: '1px dashed rgba(67,160,71,0.45)',
        }}
      >
        <p className="text-[10px] uppercase tracking-wide" style={{ color: '#5cbf63' }}>
          {mine ? `↑ ${t('mechat.paymentSent')}` : `↓ ${t('mechat.paymentReceived')}`}
        </p>
        <p className="text-white font-semibold" style={{ fontSize: 16 }}>
          {pay.quantity}
        </p>
        {pay.memo && (
          <p className="text-xs mt-0.5 break-words" style={{ color: '#9ca3af' }}>
            {pay.memo}
          </p>
        )}
        <p className="text-[10px] mt-1" style={{ color: '#6b7280' }}>
          {new Date(pay.timestamp).toLocaleString()} · {pay.contract}
        </p>
        <p className="text-[10px] mt-0.5" style={{ color: '#6b7280' }}>
          {mine ? '→' : '←'} <AccountChip account={mine ? pay.to : pay.from} />
        </p>
      </div>
      {mine ? <AccountAvatar avatarUrl={avatarUrl} size="xs" /> : null}
    </div>
  )
}

function NftPickThumb({ mediaUrl, name }: { mediaUrl: string | null; name: string }) {
  const { src, failed } = useGatewayImage(mediaUrl)
  if (!src || failed) {
    return (
      <div
        style={{
          width: 84,
          height: 84,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
          fontSize: 28,
        }}
        aria-hidden
      >
        🖼️
      </div>
    )
  }
  return (
    <img
      src={src}
      alt={name}
      loading="lazy"
      style={{ display: 'block', width: 84, height: 84, objectFit: 'cover' }}
    />
  )
}

/**
 * Grid of the account's own NFTs. Picking one appends `nft:<id>` to the draft —
 * the asset itself is not transferred, only referenced.
 */
function NftPickerSheet({
  onPick,
  onClose,
}: {
  onPick: (asset: NftAsset) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const nfts = useWalletStore(s => s.nfts)
  const nftsLoading = useWalletStore(s => s.nftsLoading)
  const refreshNfts = useWalletStore(s => s.refreshNfts)

  useEffect(() => {
    if (nfts.length === 0) void refreshNfts()
  }, [nfts.length, refreshNfts])

  const withMedia = useMemo(() => nfts.filter(n => n.mediaUrl), [nfts])

  return (
    <div className="px-4 pt-3 pb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-white text-sm font-semibold">{t('mechat.nftPickerTitle')}</span>
        <button
          type="button"
          className="btn-ghost"
          style={{ padding: '4px 10px', fontSize: 12, height: 28 }}
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <p className="text-[10px] mb-2" style={{ color: '#4b5563' }}>
        {t('mechat.nftNote')}
      </p>

      {nftsLoading && withMedia.length === 0 ? (
        <p className="text-xs py-3" style={{ color: '#6b7280' }}>
          {t('common.loading')}
        </p>
      ) : withMedia.length === 0 ? (
        <p className="text-xs py-3" style={{ color: '#6b7280' }}>
          {t('mechat.noNfts')}
        </p>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
          {withMedia.map(nft => (
            <button
              key={nft.assetId}
              type="button"
              onClick={() => onPick(nft)}
              style={{
                flexShrink: 0,
                width: 84,
                borderRadius: 10,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                background: 'rgba(0,0,0,0.35)',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <NftPickThumb mediaUrl={nft.mediaUrl} name={nft.name} />
              <span
                className="block text-[9px] truncate px-1 py-1"
                style={{ color: '#9ca3af' }}
                title={nft.name}
              >
                {nft.name}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
